// A disclosure someone can read *before* they act, attached to the control they
// are about to press.
//
// WHY NOT `title`. The rest of this app uses the native attribute, and for a
// hint about a nav link that is the right amount of machinery. It is the wrong
// tool here. A `title` opens after a browser-chosen delay, in browser-chosen
// chrome, never on touch, and never at all for a keyboard user — so the one
// sentence that says which permissions we are about to request would be
// invisible to a reviewer watching a recording, and to anyone not using a
// mouse. Meta's Platform Terms ask that people be told what an app does with
// their data at the point they grant it; a tooltip nobody can open does not
// tell anybody anything.
//
// So: opens on hover, on keyboard focus, and on click for touch. Escape closes
// it. The bubble is `role="tooltip"` and wired to the trigger with
// `aria-describedby`, which is what makes it part of the button's accessible
// description rather than a decoration beside it.

import { useCallback, useEffect, useId, useRef, useState } from "react";
import type { ReactNode } from "react";

/**
 * A paragraph inside a bubble — a `<span>` shown as a block, not a `<p>`.
 *
 * THIS IS NOT FUSSINESS. The tip has to be droppable beside whatever it
 * explains: a heading, a `<dt>`, a row of buttons. A heading's content model is
 * phrasing content only, so a `<p>` inside one closes the heading early and the
 * rest of the tip lands outside it — valid-looking in JSX, wrong in the DOM.
 * Everything the bubble renders is therefore phrasing content, and a tip can go
 * anywhere text can.
 */
export function TipP({ children, muted }: { children: ReactNode; muted?: boolean }) {
  return <span className={muted ? "tip-p muted" : "tip-p"}>{children}</span>;
}

/** A list of terms and what each one means, in the same phrasing-only shape. */
export function TipList({ children }: { children: ReactNode }) {
  return <span className="tip-list">{children}</span>;
}

export function TipRow({ term, meaning }: { term: string; meaning: ReactNode }) {
  return (
    <span className="tip-row">
      <code>{term}</code>
      <span className="muted">{meaning}</span>
    </span>
  );
}

export function InfoTip({
  label,
  children,
  align = "start",
}: {
  /** What the trigger announces — "What Facebook permissions this asks for". */
  label: string;
  children: ReactNode;
  /** Which edge the bubble hangs from, so it never leaves the card. */
  align?: "start" | "end";
}) {
  const id = useId();
  const [open, setOpen] = useState(false);
  const wrap = useRef<HTMLSpanElement>(null);

  // Click-outside and Escape, but only while something is open: a listener per
  // tooltip on every page click would be a lot of nothing happening.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    const onDown = (e: MouseEvent) => {
      if (!wrap.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onDown);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onDown);
    };
  }, [open]);

  const close = useCallback(() => setOpen(false), []);

  return (
    // Hover and focus-within are handled in CSS so the bubble opens without a
    // render; `open` exists for touch, where neither event ever fires.
    <span className={`infotip${open ? " open" : ""} align-${align}`} ref={wrap}
          onMouseLeave={close}>
      <button type="button"
              className="infotip-trigger"
              aria-describedby={id}
              aria-expanded={open}
              aria-label={label}
              onClick={() => setOpen((v) => !v)}>
        <span aria-hidden="true">i</span>
      </button>
      {/* Always in the DOM, never `display:none` while closed — a tooltip the
          screen reader can only reach when a sighted user has hovered is not a
          description, it is a secret. Visibility is the CSS's business. */}
      <span className="infotip-bubble" id={id} role="tooltip">{children}</span>
    </span>
  );
}
